/**
 * A file or folder name as people typed it, shown so mixed Arabic and Latin reads right
 * (2026-10-15). "بريف الأميسون.docx" used to show as "docx.بريف الأميسون": in a
 * right-to-left run the extension, a Latin word after a neutral dot, lands on the far
 * left. The name is isolated from the text around it (<bdi>, its own direction), and the
 * extension is a separate left-to-right span that always sits at the end, the way a file
 * manager shows it. Use these everywhere a user-provided file or folder name is shown.
 */
import { cn } from '@/lib/cn';

/** "name.ext" as base and extension; no extension for ".DS_Store" or "README". */
export function splitName(name: string): [string, string] {
  const i = name.lastIndexOf('.');
  return i > 0 && i < name.length - 1 && name.length - i <= 8 ? [name.slice(0, i), name.slice(i)] : [name, ''];
}

export function FileName({ name, className, title }: { name: string | null | undefined; className?: string; title?: string }) {
  const n = name ?? '';
  const [base, ext] = splitName(n);
  return (
    <span dir="ltr" title={title ?? n} data-testid="file-name" className={cn('[unicode-bidi:isolate]', className)}>
      <bdi dir="auto">{base}</bdi>{ext && <span dir="ltr">{ext}</span>}
    </span>
  );
}

/** A folder path, each folder isolated, joined with " / ". */
export function FolderPath({ path, className }: { path: string | null | undefined; className?: string }) {
  const segs = (path ?? '').split('/').filter(Boolean);
  return (
    <span dir="auto" title={segs.join(' / ')} data-testid="folder-path" className={cn('[unicode-bidi:isolate]', className)}>
      {segs.map((s, i) => <span key={i}>{i > 0 && ' / '}<bdi dir="auto">{s}</bdi></span>)}
    </span>
  );
}

/** Any other user-provided text (a title, a value), isolated with its own direction. */
export function Iso({ children, className }: { children: React.ReactNode; className?: string }) {
  return <bdi dir="auto" className={className}>{children}</bdi>;
}
